/**
 * Therapist Practice History — "My Practice" view.
 *
 * Lists the therapist's own past practice sessions, scoring (subject to
 * the supervisor-gating policy), and links into the formulation /
 * reflection / feedback pages for any session.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Stack, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, IconButton, Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  PlayArrow as PlayIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { verdictColor } from '../../utils/trainingVerdict';

const formatDate = (s) => s ? new Date(s).toLocaleString() : '—';
const formatDuration = (sec) => sec ? `${Math.round(sec / 60)} min` : '—';

const SCORING_LABEL = {
  visible: { label: 'Released', color: 'success' },
  pending: { label: 'Evaluating…', color: 'default' },
  awaiting: { label: 'Awaiting supervisor', color: 'info' },
  'qa-only': { label: 'QA only', color: 'default' },
};

export default function TherapistPracticeHistory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const token = localStorage.getItem('token');

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/simulation-governance/therapist/practice-sessions', token);
      const body = res?.data || res;
      setSessions(body?.sessions || []);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Aggregate scored sessions by scenario for a tiny "best score" sidebar.
  // Only count sessions whose scoring is visible to the therapist.
  const byScenario = new Map();
  for (const s of sessions) {
    if (!s._scoreVisible || s.aiEvaluation?.compositePercentage == null) continue;
    const k = String(s.scenarioId || s._scenarioName);
    const cur = byScenario.get(k) || { name: s._scenarioName, attempts: 0, best: 0, last: null };
    cur.attempts += 1;
    cur.best = Math.max(cur.best, s.aiEvaluation.compositePercentage);
    cur.last = s.aiEvaluation.compositePercentage;
    byScenario.set(k, cur);
  }
  const scoredScenarios = Array.from(byScenario.values());

  const goToFeedback = (sid) => navigate(`/training/session/${sid}/feedback`);
  const goToFormulation = (sid) => navigate(`/training/session/${sid}/formulation`);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/therapist/simulation-lab')} size="small">
          <BackIcon />
        </IconButton>
        <HistoryIcon color="primary" />
        <Typography variant="h5" sx={{ flex: 1 }}>My practice history</Typography>
        <Tooltip title="Refresh"><IconButton onClick={load} size="small"><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Your previous practice sessions. Detailed AI scoring is shown when released by your supervisor
        (or immediately if your group is on <em>auto</em> visibility).
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {scoredScenarios.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Per-scenario summary (visible scores only)</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {scoredScenarios.map((sc, i) => (
              <Chip
                key={i}
                size="small"
                variant="outlined"
                label={`${sc.name || 'Scenario'} — best ${Math.round(sc.best)}% (${sc.attempts} attempt${sc.attempts === 1 ? '' : 's'})`}
              />
            ))}
          </Stack>
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Scenario</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                      No practice sessions yet. Start one from the Simulation Lab.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<PlayIcon />}
                      onClick={() => navigate('/therapist/simulation-lab')}
                    >
                      Go to Simulation Lab
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              {sessions.map((s) => {
                const scoringMeta = SCORING_LABEL[s._scoringStatus] || SCORING_LABEL.pending;
                const isInProgress = s.status === 'in_progress';
                return (
                  <TableRow key={s._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{s._scenarioName || '—'}</Typography>
                      {s._scenarioDifficulty && (
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                          {s._scenarioDifficulty}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={s.status?.replace('_', ' ') || '—'}
                        color={s.status === 'completed' ? 'success' : isInProgress ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {s._scoreVisible && s.aiEvaluation?.compositePercentage != null ? (
                        <Chip
                          size="small"
                          label={`${Math.round(s.aiEvaluation.compositePercentage)}%`}
                          color={verdictColor(s.aiEvaluation.passFail)}
                          variant="outlined"
                        />
                      ) : (
                        <Chip size="small" label={scoringMeta.label} color={scoringMeta.color} variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(s.startedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDuration(s.durationSeconds)}</TableCell>
                    <TableCell align="right">
                      {isInProgress ? (
                        <Button size="small" variant="contained" startIcon={<PlayIcon />}
                          onClick={() => navigate(`/training/session/${s._id}`)}>
                          Resume
                        </Button>
                      ) : (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" variant="text" startIcon={<OpenIcon />}
                            onClick={() => goToFormulation(s._id)}>
                            Formulation
                          </Button>
                          <Button size="small" variant="outlined" startIcon={<OpenIcon />}
                            onClick={() => goToFeedback(s._id)}>
                            View
                          </Button>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
